import React from "react";
import { Button, notification } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useRegisterUserMutation } from "store/api";
import InputField from "components/Common/InputField";
import { Formik } from "formik";
import * as Yup from "yup";
import { ErrorAlert, Logo } from "components/Common";

const Register = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [registerUser, { isLoading }] = useRegisterUserMutation();

  const onFinish = async (values: any) => {
    try {
      const res = await registerUser(values).unwrap();

      notification.success({
        message: "Registration Success",
        description: res?.message,
      });
      navigate("/");
    } catch (error) {
      setError("Registration failed. Please try again.");
    }
  };

  return (
    <div>
      <Logo />
      <h1 className="text-2xl font-bold mb-1 text-center">Create an account</h1>
      <p className="mb-4 text-center">
        Enter your details below to create your account
      </p>

      <Formik
        validateOnBlur
        initialValues={{
          firstName: "",
          lastName: "",
          password: "",
          email: "",
          phone: "",
          confirmPassword: "",
          serviceProvider:
            window.location.hostname
              .replace(/-/g, " ")
              .replace(/.nevasacomptech.com/g, " ") ?? "",
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().required("First Name is required"),
          lastName: Yup.string().required("Last Name is required"),
          email: Yup.string()
            .email("Invalid email address")
            .required("Email is required"),
          phone: Yup.string()
            .required("Phone number is required")
            .min(10, "Phone number should be at least 10 digits")
            .max(13, "Only provide last 9 digits of your phone number"),
          password: Yup.string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
              "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
            ),
          confirmPassword: Yup.string()
            .required("Confirm password is required")
            .oneOf([Yup.ref("password"), ""], "Passwords don't match"),
        })}
        onSubmit={async (values) => {
          await onFinish({
            username: values.firstName + " " + values.lastName,
            ...values,
          });
        }}
      >
        {({ handleSubmit, isValid, dirty }) => (
          <form onSubmit={handleSubmit} className="space-y-3">
            <ErrorAlert type="error" message={error} setMessage={setError} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {" "}
              <InputField
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter first name"
              />
              <InputField
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Enter last name"
              />
            </div>

            <InputField
              label="Email"
              name="email"
              type="email"
              placeholder="Enter email"
            />
            <InputField
              label="Phone Number"
              name="phone"
              type="tel"
              placeholder="Enter phone number"
            />
            <InputField
              label="Password"
              name="password"
              type="password"
              placeholder="Enter password"
            />
            <InputField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              placeholder="Confirm password"
            />

            <Button
              type="primary"
              htmlType="submit"
              className="w-full h-10"
              loading={isLoading}
              disabled={!isValid || !dirty || isLoading}
            >
              Create Account
            </Button>
          </form>
        )}
      </Formik>
      <div className="flex items-center justify-end p-3 gap-2">
        Already have an account?{" "}
        <Link to="/" className="text-blue-500">
          Login
        </Link>
      </div>
    </div>
  );
};

export default Register;
